import { Loader } from '@mantine/core';
import {
  formatDateWithTimezone,
  getDateUtil,
  parseDateISO,
  tryParseDateISO,
} from '@zorro/shared/formatters';
import {
  DateInput,
  Group,
  MonthPickerInput,
  Stack,
  Text,
} from '@zorro/web/design-system';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { useMemo } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { useAnalytics } from '../Analytics';
import { CoveragePeriodFormFields } from './CoveragePeriodFormUtils';

const dateUtil = getDateUtil();

type Props = {
  employeeId: string;
  onboardingPeriodId: string;
  coveragePeriodForm: UseFormReturn<CoveragePeriodFormFields>;
};

export const CoveragePeriodInputs = ({
  employeeId,
  onboardingPeriodId,
  coveragePeriodForm,
}: Props) => {
  'use no memo';
  const { sendEvent } = useAnalytics();
  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindOne',
    params: [employeeId, onboardingPeriodId],
  });

  const [enrollmentEffectiveFrom, enrollmentEffectiveUntil] = [
    tryParseDateISO(onboardingPeriod?.enrollmentEffectiveFrom),
    tryParseDateISO(onboardingPeriod?.enrollmentEffectiveUntil),
  ];

  const { watch, control, formState } = coveragePeriodForm;

  const watchedEffectiveFrom = watch('effectiveFrom');
  const watchedEffectiveUntil = watch('effectiveUntil');

  const minDate =
    employee?.eligibleFrom && enrollmentEffectiveFrom
      ? dateUtil.max(
          enrollmentEffectiveFrom,
          parseDateISO(employee.eligibleFrom)
        )
      : enrollmentEffectiveFrom;

  const maxDate =
    employee?.eligibleUntil && enrollmentEffectiveUntil
      ? dateUtil.min(
          enrollmentEffectiveUntil,
          parseDateISO(employee?.eligibleUntil)
        )
      : enrollmentEffectiveUntil;

  const effectiveUntilMinDate = useMemo(() => {
    if (
      watchedEffectiveFrom &&
      minDate &&
      watchedEffectiveFrom > minDate.toDate()
    ) {
      return watchedEffectiveFrom;
    }
    return minDate?.toDate();
  }, [watchedEffectiveFrom, minDate]);

  const effectiveFromMaxDate = useMemo(() => {
    if (
      watchedEffectiveUntil &&
      maxDate &&
      watchedEffectiveUntil < maxDate.toDate()
    ) {
      return watchedEffectiveUntil;
    }
    return maxDate?.toDate();
  }, [watchedEffectiveUntil, maxDate]);

  const InputComponent = onboardingPeriod?.isSpecialEnrollment
    ? DateInput
    : MonthPickerInput;

  if (!employee || !onboardingPeriod) return <Loader />;

  const sendCoveragePeriodEvent = async (
    eventName: string,
    defaultValue: Date | undefined | null,
    watchedValue: Date | null
  ) => {
    const fromDate = defaultValue ? formatDateWithTimezone(defaultValue) : '';
    const toDate = watchedValue ? formatDateWithTimezone(watchedValue) : '';

    if (fromDate !== toDate) {
      await sendEvent(eventName, {
        onboarding_period_id: onboardingPeriod.id,
        from_date: fromDate,
        to_date: toDate,
      });
    }
  };

  return (
    onboardingPeriod && (
      <Group gap="md" maw="680" justify="space-between" wrap="nowrap">
        <Stack w="312">
          <Controller
            control={control}
            name="effectiveFrom"
            render={({ field: { ...rest } }) => (
              <InputComponent
                {...rest}
                minDate={minDate?.toDate()}
                maxDate={effectiveFromMaxDate}
                size="md"
                onBlur={async () =>
                  sendCoveragePeriodEvent(
                    'edit_start_coverage_date',
                    formState.defaultValues?.effectiveFrom,
                    watchedEffectiveFrom
                  )
                }
              />
            )}
          />
        </Stack>

        <Text> - </Text>

        <Stack w="312">
          <Controller
            control={control}
            name="effectiveUntil"
            render={({ field: { ...rest } }) => (
              <InputComponent
                {...rest}
                minDate={effectiveUntilMinDate}
                maxDate={maxDate?.toDate()}
                size="md"
                onBlur={async () =>
                  sendCoveragePeriodEvent(
                    'edit_end_coverage_date',
                    formState.defaultValues?.effectiveUntil,
                    watchedEffectiveUntil
                  )
                }
                shouldUseEndOfMonth
              />
            )}
          />
        </Stack>
      </Group>
    )
  );
};
